import SEO from "../components/SEO";
import { Check, Zap, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "sonner";
import api from "../lib/api";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const TIERS = [
  {
    name: "Free",
    tier: "free",
    price: 0,
    priceAnnual: 0,
    priceIdMonthly: null,
    priceIdAnnual: null,
    description: "For explorers getting started with DeFi staking",
    features: [
      "Browse all 156+ staking protocols",
      "Compare up to 3 protocols side-by-side",
      "1 active APY alert",
      "Protocol intelligence grades",
      "Basic staking calculator (2 protocols, up to 3yr)",
      "12-point APY history per protocol",
      "Full educational content access",
      "Community support",
    ],
    cta: "Get Started",
    highlighted: false,
  },
  {
    name: "Starter",
    tier: "starter",
    price: 4.99,
    priceAnnual: 49.99,
    priceIdMonthly: "price_1TaRBN8BGmecqAXMn64sLOHS",
    priceIdAnnual: "price_1TaRBN8BGmecqAXMrWhhZHpg",
    description: "For individual stakers exploring opportunities",
    features: [
      "Everything in Free",
      "Compare up to 5 protocols side-by-side",
      "Unlimited APY alerts with email notifications",
      "Full staking calculator (5 protocols, all time horizons 1–10yr)",
      "48-point APY history per protocol",
      "TVL tier classification across all protocols",
      "APY stability scoring",
      "Ad-free experience",
      "Email support",
    ],
    cta: "Start Free Trial",
    highlighted: false,
  },
  {
    name: "Pro",
    tier: "pro",
    price: 9.99,
    priceAnnual: 99,
    priceIdMonthly: "price_1TCE7s8BGmecqAXMPfa5TBzu",
    priceIdAnnual: "price_1T6vJ08BGmecqAXMcefzgEEo",
    description: "For serious stakers maximizing returns",
    features: [
      "Everything in Starter",
      "Protocol risk scoring",
      "Volatility-adjusted yields",
      "Sweet Spot Scanner",
      "TVL velocity tracking",
      "Downloadable staking income report (CSV, organized by tax year)",
      "Risk-adjusted return analytics per protocol",
      "Priority support",
      "Early access to new features",
    ],
    cta: "Start Free Trial",
    highlighted: true,
  },
  {
    name: "Premium",
    tier: "premium",
    price: 19.99,
    priceAnnual: 199,
    priceIdMonthly: "price_1TCE998BGmecqAXMYVyUPujs",
    priceIdAnnual: "price_1T6vQX8BGmecqAXMAtQ1IP2j",
    description: "For professional investors and power users",
    features: [
      "Everything in Pro",
      "Full Protocol Intelligence Dashboard",
      "API access to intelligence data",
      "Full API access to ResidualVault yield data",
      "Custom data exports and advanced reporting",
      "Portfolio optimization tools",
      "Priority Slack/Discord community access",
      "Monthly group strategy Q&A",
    ],
    cta: "Start Free Trial",
    highlighted: false,
  },
];

export default function PricingPage() {
  const { isAuthenticated, user } = useAuthStore();
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);

  const handleSubscribe = async (priceId: string, tierObj: any) => {
    if (!isAuthenticated) {
      toast.error("Please sign in to subscribe");
      return;
    }
    setLoading(tierObj.name);
    try {
      const response = await api.post("/subscriptions/checkout", {
        priceId,
        tier: tierObj.tier,
      });
      const sessionId = response.data.data.sessionId;
      if (!sessionId) throw new Error("No checkout session ID received");
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to load");
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) toast.error(error.message || "Failed to start checkout");
    } catch (error: any) {
      toast.error(
        error.response?.data?.error?.message ||
          error.message ||
          "Failed to start subscription"
      );
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-navy-dark py-16">
      <SEO
        title="Pricing — Free, Starter, Pro & Premium Plans"
        description="ResidualVault is free to start. Upgrade to Starter, Pro, or Premium for advanced staking analytics, unlimited alerts, full APY history, and API access across 156+ protocols."
        canonical="https://www.residualvault.com/pricing"
        keywords="residual vault pricing, crypto staking platform pricing, DeFi tools pricing, staking analytics subscription"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-cream">Simple, </span>
            <span className="gradient-text-neon">Transparent </span>
            <span className="text-cream">Pricing</span>
          </h1>
          <p className="text-xl text-sky-blue max-w-2xl mx-auto mb-8">
            Choose the plan that is right for you. All plans include access to
            our complete staking database.
          </p>
          <div className="inline-flex items-center gap-4 p-1 bg-steel-blue/20 rounded-lg border border-sky-blue/30">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${!isAnnual ? "bg-gradient-cta text-white shadow-neon" : "text-sky-blue hover:text-cream"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${isAnnual ? "bg-gradient-cta text-white shadow-neon" : "text-sky-blue hover:text-cream"}`}
            >
              Annual{" "}
              <span className="text-xs ml-1">(Save up to 17%)</span>
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {TIERS.map((tier) => {
            const displayPrice = isAnnual ? tier.priceAnnual : tier.price;
            const priceId = isAnnual ? tier.priceIdAnnual : tier.priceIdMonthly;
            const monthlySavings: number =
              isAnnual && tier.priceAnnual > 0
                ? Number(
                    (
                      ((tier.price * 12 - tier.priceAnnual) /
                        (tier.price * 12)) *
                      100
                    ).toFixed(0)
                  )
                : 0;
            const isCurrentPlan =
              user?.subscriptionTier === tier.tier;
            const isLoading = loading === tier.name;
            return (
              <div
                key={tier.name}
                id={tier.tier}
                className={
                  tier.highlighted
                    ? "card-premium ring-2 ring-electric-blue shadow-neon-lg scale-105 relative"
                    : "card-premium relative"
                }
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-cta text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1 shadow-neon">
                      <Zap className="w-4 h-4" /> Most Popular
                    </div>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-cream mb-2">
                    {tier.name}
                  </h3>
                  <div className="mb-4">
                    {displayPrice === 0 ? (
                      <span className="text-5xl font-bold gradient-text-neon">
                        Free
                      </span>
                    ) : (
                      <span className="text-sky-blue">
                        <span className="text-5xl font-bold gradient-text-neon">
                          ${displayPrice}
                        </span>
                        /{isAnnual ? "year" : "month"}
                      </span>
                    )}
                    {isAnnual && monthlySavings > 0 && (
                      <div className="text-xs text-green-400 mt-1">
                        Save {monthlySavings}%
                      </div>
                    )}
                  </div>
                  <p className="text-sky-blue text-sm">{tier.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-cream text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                {isCurrentPlan ? (
                  <button
                    disabled
                    className="block w-full py-3 rounded-lg font-semibold text-center bg-steel-blue/20 text-sky-blue border border-sky-blue/30 cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : tier.price === 0 ? (
                  <Link
                    to={isAuthenticated ? "/dashboard" : "/register"}
                    className="bg-steel-blue/20 text-sky-blue border border-sky-blue/30 hover:bg-steel-blue/30 block w-full py-3 rounded-lg font-semibold text-center transition-all"
                  >
                    {tier.cta}
                  </Link>
                ) : priceId ? (
                  <button
                    onClick={() => handleSubscribe(priceId, tier)}
                    disabled={isLoading}
                    className={
                      tier.highlighted
                        ? "btn-neon block w-full py-3 rounded-lg font-semibold text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        : "bg-steel-blue/20 text-sky-blue border border-sky-blue/30 hover:bg-steel-blue/30 block w-full py-3 rounded-lg font-semibold text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    }
                  >
                    {isLoading ? "Loading..." : tier.cta}
                  </button>
                ) : (
                  <Link
                    to={isAuthenticated ? "/dashboard" : "/register"}
                    className={
                      tier.highlighted
                        ? "btn-neon block w-full py-3 rounded-lg font-semibold text-center transition-all"
                        : "bg-steel-blue/20 text-sky-blue border border-sky-blue/30 hover:bg-steel-blue/30 block w-full py-3 rounded-lg font-semibold text-center transition-all"
                    }
                  >
                    {tier.cta}
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Enterprise callout */}
        <div className="max-w-3xl mx-auto mb-16 p-6 bg-gradient-to-r from-electric-blue/10 to-neon-red/10 border border-electric-blue/30 rounded-2xl text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Mail className="w-6 h-6 text-electric-blue" />
            <h3 className="text-xl font-bold text-cream">Enterprise Data Plans</h3>
          </div>
          <p className="text-sky-blue mb-4">
            Need API access for your fund or fintech product? Enterprise data plans available.
          </p>
          <a
            href="mailto:support@residualvault.com"
            className="btn-neon inline-block px-6 py-3"
          >
            Contact support@residualvault.com
          </a>
        </div>

        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            <span className="text-cream">Frequently Asked </span>
            <span className="gradient-text-neon">Questions</span>
          </h2>
          <div className="space-y-4">
            <div className="card-glass">
              <h3 className="text-lg font-semibold text-cream mb-2">
                Can I change plans anytime?
              </h3>
              <p className="text-sky-blue">
                Yes! You can upgrade or downgrade your plan at any time. Changes
                take effect immediately.
              </p>
            </div>
            <div className="card-glass">
              <h3 className="text-lg font-semibold text-cream mb-2">
                Do you offer refunds?
              </h3>
              <p className="text-sky-blue">
                We offer a 30-day money-back guarantee on all paid plans. If you
                are not satisfied, contact us for a full refund.
              </p>
            </div>
            <div className="card-glass">
              <h3 className="text-lg font-semibold text-cream mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-sky-blue">
                We accept all major credit cards (Visa, Mastercard, Amex) and
                cryptocurrency payments.
              </p>
            </div>
            <div className="card-glass">
              <h3 className="text-lg font-semibold text-cream mb-2">
                Is there a free trial for paid plans?
              </h3>
              <p className="text-sky-blue">
                Yes! All paid plans include a 7-day free trial. No credit card
                required to start.
              </p>
            </div>
            <div className="card-glass">
              <h3 className="text-lg font-semibold text-cream mb-2">
                What happens when I switch from monthly to annual?
              </h3>
              <p className="text-sky-blue">
                You will receive a prorated credit for your remaining monthly
                subscription and be charged the annual rate immediately. You
                save up to 17% with annual billing!
              </p>
            </div>
          </div>
        </div>
        <div className="card-premium text-center max-w-3xl mx-auto mt-16">
          <h2 className="text-3xl font-bold mb-4">
            <span className="text-cream">Still have </span>
            <span className="gradient-text-neon">questions?</span>
          </h2>
          <p className="text-sky-blue mb-6">
            Our team is here to help you choose the right plan for your needs.
          </p>
          <a
            href="mailto:support@residualvault.com"
            className="btn-neon inline-block"
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
}
